import { Component, forwardRef, Inject, OnInit } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { ActionPriority, ActionState, ActionTopic } from "../../../model";
import { ActionDefinitionTypeService } from "../../../service/action-definition-type.service";
import { ActionDefinitionService } from "../../../service/action-definition.service";
import { CustomLabelService } from "../../../service/custom-label.service";
import { CustomPropertyService } from "../../../service/custom-property.service";
import { DateRangeService } from "../../../service/date-range.service";
import { ActionAdvancedSearchComponent } from "../../advanced-search/action-advanced-search/action-advanced-search.component";
import { CapitalizePipe } from "../../pipe";
import { SearchFieldService } from "../search-field.service";
import { AbstractPropertySearchInputComponent } from "../search-input/abstract-property-search-input.component";

@Component({
    selector: 'actions-property-search-input',
    template: require('./actions-property-search-input.component.html'),
    providers: [SearchFieldService]
})
export class ActionsPropertySearchInputComponent extends AbstractPropertySearchInputComponent implements OnInit {

    constructor(
        @Inject(forwardRef(() => CustomPropertyService)) customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => CustomLabelService)) customLabelService: CustomLabelService,
        @Inject(forwardRef(() => ActionDefinitionService)) private actionDefinitionService: ActionDefinitionService,
        @Inject(forwardRef(() => SearchFieldService)) protected searchFieldService: SearchFieldService,
        @Inject(forwardRef(() => CapitalizePipe)) protected capitalizePipe: CapitalizePipe,
        @Inject(forwardRef(() => DateRangeService)) private dateRangeService: DateRangeService,
        @Inject(forwardRef(() => ActionDefinitionTypeService)) private actionDefinitionTypeService: ActionDefinitionTypeService,
    ) {
        super(customPropertyService, customLabelService, searchFieldService);
    }

    initConfiguration(): void {
        this.defaultProperties = ActionAdvancedSearchComponent.DEFAULT_SEARCHABLE_PROPERTIES;
        if (this.property?.indexOf('properties.') > -1 || this.defaultProperties.some(prop => prop.name == this.property)) {
            if (this.property.startsWith("properties.")) {
                this.property = this.property.substring(11);
            }
            this.searchFields = [this.property];
        } else {
            this.useDefaultProperty()
        }
    }

    protected handleDefaultProperty(): void {
        if (this.property == "actionDefinitions") {
            this.handleActionDefinitions();
        } else if (this.property == "statuses" || this.property == "status") {
            this.handleStatuses();
        } else if (this.property == "priorities" || this.property == "actionDefinition.priority") {
            this.handlePriorities();
        } else if (this.property == "period") {
            this.handlePeriod();
        } else if (this.property == "topics" || this.property == "actionDefinition.topic") {
            this.handleTopics();
        } else if (this.property == "actionDefinitionTypes" || this.property == "actionDefinition.type.code") {
            this.handleActionDefinitionTypes();
        }
    }

    private handleActionDefinitions(): void {
        this.actionDefinitionService.getRecursivelyAll().then(alertDefs => {
            const actionDefinitionTypes = alertDefs.map(def => { return { value: def.id, label: def.label || def.name } }).sort((a, b) => (a.label > b.label) ? 1 : -1);
            if (actionDefinitionTypes?.length) {
                this.advancedSearchConfiguration = [{ name: 'actionDefinitions', placeholder: "All Actions", type: 'STRING', selectionMode: 'MAT_SELECTION', values: actionDefinitionTypes, value: this.searchFieldService.getValueFromFieldsValues('actionDefinitions', this.savedFieldsValues), multipleSelection: true, hideLabel: true }];
            }
        }).catch(err => console.error(err));
    }

    private handleStatuses(): void {
        const statuses: { value: string, label: string }[] = Object.values(ActionState).filter(s => s != ActionState.TODO).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });
        this.advancedSearchConfiguration = [{ name: 'statuses', placeholder: "All Statuses", type: 'STRING', selectionMode: 'MAT_SELECTION', values: statuses, value: this.searchFieldService.getValueFromFieldsValues('statuses', this.savedFieldsValues), multipleSelection: true, hideLabel: true }];
    }

    private handleTopics(): void {
        const topics: { value: string, label: string }[] = Object.values(ActionTopic).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });
        this.advancedSearchConfiguration = [{ name: 'topics', placeholder: "All Topics", type: 'STRING', selectionMode: 'MAT_SELECTION', values: topics, value: this.searchFieldService.getValueFromFieldsValues('topics', this.savedFieldsValues), multipleSelection: true, hideLabel: true }];
    }

    private handlePriorities(): void {
        const priorities: { value: string, label: string }[] = Object.values(ActionPriority).map(t => { return { label: this.capitalizePipe.transform(t), value: t } });
        this.advancedSearchConfiguration = [{ name: 'priorities', placeholder: "All Priorities", type: 'STRING', selectionMode: 'MAT_SELECTION', values: priorities, value: this.searchFieldService.getValueFromFieldsValues('priorities', this.savedFieldsValues), multipleSelection: true, hideLabel: true }];
    }

    private handlePeriod(): void {
        this.advancedSearchConfiguration = [{ name: 'period', type: 'PERIOD', value: this.getDateRange(), hideLabel: true }];
    }

    private handleActionDefinitionTypes(): void {
        this.actionDefinitionTypeService.getAllActionDefinitionTypes().then(actionTypes => {
            const actionDefinitionTypes = actionTypes.map(t => { return { value: t.value, label: t.label } }).sort((a, b) => (a.label > b.label) ? 1 : -1);
            if (actionDefinitionTypes?.length) {
                this.advancedSearchConfiguration = [{ name: 'actionDefinitionTypes', placeholder: "All Types", type: 'STRING', selectionMode: 'MAT_SELECTION', values: actionDefinitionTypes, value: this.searchFieldService.getValueFromFieldsValues('actionDefinitionTypes', this.savedFieldsValues), multipleSelection: true, hideLabel: true }];
            }
        }).catch(err => console.error(err));
    }

    protected handleValue(body: any, isEncoded: boolean): any {
        if (this.property == 'period') {
            const rangeValue: { range: DateRange<moment.Moment>, rangeName: string } = body['period'];
            const range: DateRange<moment.Moment> = rangeValue?.rangeName ? this.dateRangeService.getCustomDateRangeByName(rangeValue.rangeName)?.range : rangeValue?.range;
            delete body['period'];
            body['rangeName'] = rangeValue?.rangeName;
            body['startTimestamp'] = range?.start?.valueOf();
            body['endTimestamp'] = range?.end?.valueOf();
        }
        return body;
    }

}